import { useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { CONSTANTS } from "@constants/index";
import { useLocationContext } from "@contexts/location/LocationContext";
import { ParticipantType } from "@models/components/booking.model";
import { BookedDataType } from "@models/components/user.model";
import {
  DefaultType,
  TerminologyLocaleType,
} from "@models/utils/localisation.models";
import API_URL from "@services/apiConfig/api.config";
import { applyLocalization } from "@services/common.service";
import {
  handleCopy as utilHandleCopy,
  handleEmail as utilHandleEmail,
  handleShare as utilHandleShare,
  handleWhatsApp as utilHandleWhatsApp,
} from "@utils/share.utils";

interface UseBookingShareReturn {
  calendarEventData: BookedDataType | null;
  copied: boolean;
  handleCopy: () => void;
  handleEmail: () => void;
  handleShare: (e: React.MouseEvent) => Promise<void>;
  handleWhatsApp: () => void;
  setShowSharePopover: (open: boolean) => void;
  shareText: string;
  shareTitle: string;
  shareUrl: string;
  showSharePopover: boolean;
}

const getDefaultTitle = (titleDefault?: string | DefaultType): string => {
  if (typeof titleDefault === "string") {
    return titleDefault;
  }
  if (titleDefault && typeof titleDefault === "object") {
    const val = Object.values(titleDefault)[0];
    return typeof val === "string" ? val : "";
  }
  return "";
};

const getLocales = (
  locales?: DefaultType | TerminologyLocaleType
): { [key: string]: string } => {
  if (!locales || typeof locales !== "object") return {};
  const result: { [key: string]: string } = {};
  for (const [key, value] of Object.entries(locales)) {
    if (typeof value === "string") {
      result[key] = value;
    } else if (value && typeof value === "object") {
      const valStr = Object.values(value)[0];
      result[key] = typeof valStr === "string" ? valStr : "";
    }
  }
  return result;
};

export const useBookingShare = (
  participant: ParticipantType | null
): UseBookingShareReturn => {
  const { t } = useTranslation();
  const { activeLocation, locationDetails } = useLocationContext();
  const [showSharePopover, setShowSharePopover] = useState(false);
  const [copied, setCopied] = useState(false);

  const calendarEventData: BookedDataType | null = participant
    ? {
        bookingId: participant.bookingId,
        club: {
          id: locationDetails?.clubId || "",
          title: {
            default:
              getDefaultTitle(participant.club?.title?.default) ||
              getDefaultTitle(locationDetails?.title?.default),
            locales:
              getLocales(participant.club?.title?.locales) ||
              getLocales(locationDetails?.title?.locales),
          },
        },
        date: moment(participant.startTime).format("YYYY-MM-DD"),
        endDate: moment(participant.endTime).format("YYYY-MM-DD"),
        endTime: moment(participant.endTime).format("HH:mm"),
        location: {
          address: { default: "", locales: { en: "" } },
          id: "",
          map: { latitude: 0, longitude: 0 },
          mapLocality: "",
          mapLocation: "",
          resourceType: {
            terminology: {
              plural: { default: "", locales: {} },
              singular: { default: "", locales: {} },
            },
          },
          title: { default: "", locales: { en: "" } },
        },
        resource: {
          id: participant.resource?.id || "",
          title: {
            default: getDefaultTitle(participant.resource?.title?.default),
            locales: getLocales(participant.resource?.title?.locales),
          },
        },
        resourceId: participant.resource?.id || "",
        role: "organizer",
        startDate: moment(participant.startTime).format("YYYY-MM-DD"),
        startTime: moment(participant.startTime).format("HH:mm"),
        title: {
          default: "",
          locales: {},
        },
      }
    : null;

  const venueName =
    (participant?.club?.title
      ? applyLocalization(participant.club.title)
      : "") ||
    (locationDetails?.title ? applyLocalization(locationDetails.title) : "") ||
    (activeLocation?.info?.title
      ? applyLocalization(activeLocation.info.title)
      : "");
  const courtName = participant?.resource?.title
    ? applyLocalization(participant.resource.title)
    : "";

  const dateStr = participant?.startTime
    ? moment(participant.startTime).format(
        CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT
      )
    : "";
  const timeStr = participant
    ? `${moment(participant.startTime).format("HH:mm")}–${moment(participant.endTime).format("HH:mm")}`
    : "";

  const shareUrl = participant
    ? `${window.location.origin}${API_URL.BOOKING.BOOKING_SHARE}/${participant.bookingId}`
    : "";
  const shareTitle = `${t("shareTextCourtBooking")}-${venueName || courtName}`;
  const shareText = `${venueName ? `${venueName}, ` : ""}${courtName} — ${dateStr}, ${timeStr}\n${shareUrl}`;
  const bodyAreaText = `${venueName ? `${venueName}, ` : ""}${courtName} — ${dateStr}, ${timeStr}`;
  const handleShare = async (e: React.MouseEvent): Promise<void> => {
    await utilHandleShare(
      bodyAreaText,
      shareTitle,
      shareUrl,
      () => setShowSharePopover(true),
      e
    );
  };

  const handleCopy = (): void => {
    utilHandleCopy(shareUrl, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const handleEmail = (): void => {
    utilHandleEmail(shareTitle, shareText);
  };

  const handleWhatsApp = (): void => {
    utilHandleWhatsApp(shareText);
  };

  return {
    calendarEventData,
    copied,
    handleCopy,
    handleEmail,
    handleShare,
    handleWhatsApp,
    setShowSharePopover,
    shareText,
    shareTitle,
    shareUrl,
    showSharePopover,
  };
};
